import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ko' ? 'en' : 'ko';
    i18n.changeLanguage(newLang);
  };

  const isHomePage = location.pathname === '/';

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-sm`}
    >
      <div className="h-16 md:h-20 px-4 md:px-8 lg:px-32 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 md:gap-3 cursor-pointer">
          <img 
            src="https://static.readdy.ai/image/89ce65a415860e3da90921dded83bf21/24be707e14ec029f6469791aa4a7f602.png"
            alt="BrainCommerce"
            className="h-7 md:h-10 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-12">
          <Link 
            to="/" 
            className="text-xs xl:text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap text-[#1a1a1a] hover:text-[#4a4a4a]"
          >
            {t('nav.home')}
          </Link>
          <Link 
            to="/about" 
            className="text-xs xl:text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap text-[#1a1a1a] hover:text-[#4a4a4a]"
          >
            {t('nav.about')}
          </Link>
          <Link 
            to="/hybrid-workforce" 
            className="text-xs xl:text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap text-[#1a1a1a] hover:text-[#4a4a4a]"
          >
            {t('nav.hybrid')}
          </Link>
          <Link 
            to="/businesses" 
            className="text-xs xl:text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap text-[#1a1a1a] hover:text-[#4a4a4a]"
          >
            {t('nav.businesses')}
          </Link>
          <Link 
            to="/solutions" 
            className="text-xs xl:text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap text-[#1a1a1a] hover:text-[#4a4a4a]"
          >
            {t('nav.solutions')}
          </Link>
          <Link 
            to="/careers" 
            className="text-xs xl:text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap text-[#1a1a1a] hover:text-[#4a4a4a]"
          >
            {t('nav.careers')}
          </Link>
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-3 md:gap-6">
          <button 
            onClick={toggleLanguage}
            className="text-xs md:text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap text-[#1a1a1a] hover:text-[#4a4a4a]"
          >
            {i18n.language === 'ko' ? 'EN' : 'KO'}
          </button>
          <Link 
            to="/contact"
            className="hidden md:inline-block px-4 md:px-6 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap text-xs md:text-sm border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white"
          >
            {t('nav.contact')}
          </Link>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer text-[#1a1a1a]"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <nav className="flex flex-col px-4 py-4">
            <Link 
              to="/" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="py-3 text-sm uppercase tracking-wider text-[#1a1a1a] hover:text-[#4a4a4a] transition-colors cursor-pointer"
            >
              {t('nav.home')}
            </Link>
            <Link 
              to="/about" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="py-3 text-sm uppercase tracking-wider text-[#1a1a1a] hover:text-[#4a4a4a] transition-colors cursor-pointer"
            >
              {t('nav.about')}
            </Link>
            <Link 
              to="/hybrid-workforce" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="py-3 text-sm uppercase tracking-wider text-[#1a1a1a] hover:text-[#4a4a4a] transition-colors cursor-pointer"
            >
              {t('nav.hybrid')}
            </Link>
            <Link 
              to="/businesses" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="py-3 text-sm uppercase tracking-wider text-[#1a1a1a] hover:text-[#4a4a4a] transition-colors cursor-pointer"
            >
              {t('nav.businesses')}
            </Link>
            <Link 
              to="/solutions" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="py-3 text-sm uppercase tracking-wider text-[#1a1a1a] hover:text-[#4a4a4a] transition-colors cursor-pointer"
            >
              {t('nav.solutions')}
            </Link>
            <Link 
              to="/careers" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="py-3 text-sm uppercase tracking-wider text-[#1a1a1a] hover:text-[#4a4a4a] transition-colors cursor-pointer"
            >
              {t('nav.careers')}
            </Link>
            <Link 
              to="/contact" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="mt-2 py-3 px-6 bg-[#1a1a1a] text-white text-sm text-center rounded-lg hover:bg-[#2a2a2a] transition-colors cursor-pointer"
            >
              {t('nav.contact')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
